<script>
    import { onMount, afterUpdate } from 'svelte';
    import { messages, isLoading, error, sendMessage, clearChat } from '../stores/chatStore.js';
    import ChatMessage from './ChatMessage.svelte';
    import MessageInput from './MessageInput.svelte';
    import SuggestionChips from './SuggestionChips.svelte';
    
    export let endpointId;
    
    let messagesContainer;
    let shouldScrollToBottom = true;
    
    // Handle sending messages
    function handleSendMessage(event) {
        const { message } = event.detail;
        sendMessage(message);
        shouldScrollToBottom = true;
    }
    
    // Handle suggestion click
    function handleSuggestionClick(suggestion) {
        sendMessage(suggestion);
        shouldScrollToBottom = true;
    }
    
    // Scroll to bottom of messages
    function scrollToBottom() {
        if (messagesContainer && shouldScrollToBottom) {
            messagesContainer.scrollTop = messagesContainer.scrollHeight;
        }
    }
    
    // Check if user has scrolled up
    function handleScroll() {
        if (messagesContainer) {
            const { scrollTop, scrollHeight, clientHeight } = messagesContainer;
            const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10;
            shouldScrollToBottom = isAtBottom;
        }
    }
    
    // Auto-scroll after messages update
    afterUpdate(() => {
        scrollToBottom();
    });
    
    // Handle clear chat
    function handleClearChat() {
        if (confirm('Möchten Sie den Chat-Verlauf wirklich löschen?')) {
            clearChat();
        }
    }
    
    // Copy endpoint ID to clipboard
    async function copyEndpointId() {
        try {
            await navigator.clipboard.writeText(endpointId);
        } catch (err) {
            console.error('Failed to copy endpoint ID:', err);
        }
    }
    
    // Format endpoint ID for display
    function formatEndpointId(id) {
        if (!id) return '';
        return `${id.substring(0, 8)}...${id.substring(id.length - 4)}`;
    }
</script>

<div class="chat-interface">
    <!-- Header -->
    <header class="chat-header">
        <div class="header-content">
            <div class="endpoint-info">
                <h1 class="chat-title">Chat Interface</h1>
                <button 
                    class="endpoint-id" 
                    on:click={copyEndpointId}
                    title="Endpoint ID kopieren"
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path>
                        <rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect>
                    </svg>
                    {formatEndpointId(endpointId)}
                </button>
            </div>
            
            <div class="header-actions">
                <button 
                    class="action-button"
                    on:click={handleClearChat}
                    title="Chat löschen"
                    disabled={$messages.length === 0}
                >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <polyline points="3,6 5,6 21,6"></polyline>
                        <path d="M19,6V20a2,2,0,0,1-2,2H7a2,2,0,0,1-2-2V6M8,6V4a2,2,0,0,1,2-2h4a2,2,0,0,1,2,2V6"></path>
                        <line x1="10" y1="11" x2="10" y2="17"></line>
                        <line x1="14" y1="11" x2="14" y2="17"></line>
                    </svg>
                </button>
            </div>
        </div>
    </header>
    
    <!-- Messages Area -->
    <main class="messages-area">
        <div 
            class="messages-container" 
            bind:this={messagesContainer}
            on:scroll={handleScroll}
        >
            {#if $messages.length === 0}
                <div class="empty-state">
                    <div class="empty-icon">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                        </svg>
                    </div>
                    <h2 class="empty-title">Willkommen beim Chat</h2>
                    <p class="empty-description">
                        Stellen Sie eine Frage oder beginnen Sie eine Unterhaltung mit der KI.
                    </p>
                </div>
            {:else}
                {#each $messages as message, index (message.id)}
                    <ChatMessage 
                        {message} 
                        isLast={index === $messages.length - 1}
                    />
                {/each}
                
                {#if $isLoading}
                    <div class="typing-indicator">
                        <div class="typing-bubble">
                            <div class="typing-dots">
                                <span></span>
                                <span></span>
                                <span></span>
                            </div>
                        </div>
                    </div>
                {/if}
            {/if}
        </div>
    </main>
    
    <!-- Error Display -->
    {#if $error}
        <div class="error-banner">
            <div class="error-content">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="15" y1="9" x2="9" y2="15"></line>
                    <line x1="9" y1="9" x2="15" y2="15"></line>
                </svg>
                <span>{$error}</span>
            </div>
            <button 
                class="error-close"
                on:click={() => error.set(null)}
                aria-label="Fehler schließen"
            >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
            </button>
        </div>
    {/if}
    
    <!-- Input Area with Suggestions -->
    <div class="input-area">
        <SuggestionChips onSuggestionClick={handleSuggestionClick} />
        <MessageInput on:send={handleSendMessage} />
    </div>
</div>

<style>
    .chat-interface {
        display: flex;
        flex-direction: column;
        height: 100vh;
        background: #1a1a1a;
    }
    
    .chat-header {
        background: #2d2d2d;
        border-bottom: 1px solid #404040;
        padding: 16px 20px;
        flex-shrink: 0;
    }
    
    .header-content {
        display: flex;
        justify-content: space-between;
        align-items: center;
        max-width: 1200px;
        margin: 0 auto;
    }
    
    .endpoint-info {
        display: flex;
        align-items: center;
        gap: 16px;
    }
    
    .chat-title {
        font-size: 1.25rem;
        font-weight: 600;
        margin: 0;
        color: #ffffff;
    }
    
    .endpoint-id {
        display: flex;
        align-items: center;
        gap: 6px;
        background: #404040;
        border: none;
        border-radius: 6px;
        padding: 6px 10px;
        font-size: 0.8rem;
        color: #ccc;
        cursor: pointer;
        transition: all 0.2s ease;
    }
    
    .endpoint-id:hover {
        background: #555;
        color: #fff;
    }
    
    .header-actions {
        display: flex;
        gap: 8px;
    }
    
    .action-button {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 40px;
        height: 40px;
        background: transparent;
        border: 1px solid #404040;
        border-radius: 8px;
        color: #ccc;
        cursor: pointer;
        transition: all 0.2s ease;
    }
    
    .action-button:hover:not(:disabled) {
        background: #404040;
        color: #fff;
        border-color: #555;
    }
    
    .action-button:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
    
    .messages-area {
        flex: 1;
        overflow: hidden;
        display: flex;
        flex-direction: column;
    }
    
    .messages-container {
        flex: 1;
        overflow-y: auto;
        padding: 20px;
        max-width: 1200px;
        margin: 0 auto;
        width: 100%;
    }
    
    .empty-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        height: 100%;
        text-align: center;
        padding: 40px 20px;
    }
    
    .empty-icon {
        margin-bottom: 24px;
        opacity: 0.6;
    }
    
    .empty-title {
        font-size: 1.5rem;
        font-weight: 600;
        margin: 0 0 12px 0;
        color: #ffffff;
    }
    
    .empty-description {
        font-size: 1rem;
        color: #888;
        margin: 0;
        max-width: 400px;
        line-height: 1.5;
    }
    
    .typing-indicator {
        display: flex;
        justify-content: flex-start;
        margin-bottom: 16px;
        animation: fadeIn 0.3s ease-out;
    }
    
    .typing-bubble {
        background: #2d2d2d;
        border: 1px solid #404040;
        border-radius: 18px;
        border-bottom-left-radius: 4px;
        padding: 12px 16px;
        max-width: 70px;
    }
    
    .typing-dots {
        display: flex;
        gap: 4px;
    }
    
    .typing-dots span {
        width: 8px;
        height: 8px;
        background: #888;
        border-radius: 50%;
        animation: typing 1.4s ease-in-out infinite;
    }
    
    .typing-dots span:nth-child(2) {
        animation-delay: 0.2s;
    }
    
    .typing-dots span:nth-child(3) {
        animation-delay: 0.4s;
    }
    
    .error-banner {
        display: flex;
        align-items: center;
        justify-content: space-between;
        background: #dc3545;
        color: white;
        padding: 12px 20px;
        margin: 0 20px 16px;
        border-radius: 8px;
        animation: slideDown 0.3s ease-out;
    }
    
    .error-content {
        display: flex;
        align-items: center;
        gap: 8px;
    }
    
    .error-close {
        background: none;
        border: none;
        color: white;
        cursor: pointer;
        padding: 4px;
        border-radius: 4px;
        transition: background-color 0.2s ease;
    }
    
    .error-close:hover {
        background: rgba(255, 255, 255, 0.1);
    }
    
    .input-area {
        flex-shrink: 0;
        padding: 0 20px 20px;
        max-width: 1200px;
        margin: 0 auto;
        width: 100%;
    }
    
    @keyframes typing {
        0%, 60%, 100% {
            transform: translateY(0);
            opacity: 0.4;
        }
        30% {
            transform: translateY(-10px);
            opacity: 1;
        }
    }
    
    @keyframes slideDown {
        from {
            transform: translateY(-100%);
            opacity: 0;
        }
        to {
            transform: translateY(0);
            opacity: 1;
        }
    }
    
    @keyframes fadeIn {
        from {
            opacity: 0;
            transform: translateY(10px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }
    
    @media (max-width: 768px) {
        .chat-header {
            padding: 12px 16px;
        }
        
        .header-content {
            flex-direction: column;
            gap: 12px;
            align-items: flex-start;
        }
        
        .endpoint-info {
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
        }
        
        .chat-title {
            font-size: 1.1rem;
        }
        
        .messages-container {
            padding: 16px;
        }
        
        .empty-state {
            padding: 20px;
        }
        
        .empty-title {
            font-size: 1.25rem;
        }
        
        .error-banner {
            margin: 0 16px 12px;
            padding: 10px 16px;
        }
        
        .input-area {
            padding: 0 16px 16px;
        }
    }
</style>
